import styled from 'styled-components'
import { Icon } from '../icon'
import { Button } from '../button'
import { AsyncSelectProps } from './async-select.types'
import { Combobox } from '@headlessui/react'
import { Spinner } from '../spinner'
import { Spacer } from '../spacer'

import {
  StyledSelect,
  StyledSelectTrigger,
  StyledSelectTriggerDropdown,
  StyledSelectList,
  StyledSelectOptions,
  StyledSelectAddOptionButton,
  StyledSelectFooter,
} from './components'
import { SelectOption } from './option.types'
import { useAsyncSelect } from './hooks/useAsyncSelect'
import { Typography } from '../typography'
import { useRef, useState } from 'react'
import { BorderRadius } from '../../theme/theme'

const StyledSelectLoading = styled.div`
  ${({ theme }) => `
    padding: ${theme.sizes[3]};
    color: ${theme.colors.gray900};
    display: flex;
    border-top: 1px solid ${theme.colors.gray80};
    align-items: center;
  `}
`

const StyledClearButton = styled.div`
  ${({ theme }) => `
    display: flex;
    align-items: center;
    justify-content: center;

    button {
      padding: 0 0.6rem;
    }

    svg {
      color: ${theme.colors.gray500};
      width: ${theme.sizes[6]};
      height: ${theme.sizes[6]};
    }
  `}
`

const compareOptions = (
  a: SelectOption | null,
  b: SelectOption | null
): boolean => a?.value.toLowerCase() === b?.value.toLowerCase()

const SelectBody = ({
  open,
  selectOptions,
  selectProps,
  dataTestId,
  disabled,
  borderRadiiSize,
  clearable,
  onClear,
}: {
  open: boolean
  selectOptions: ReturnType<typeof useAsyncSelect>
  selectProps: AsyncSelectProps
  activeOption?: SelectOption
  dataTestId?: string
  disabled?: boolean
  borderRadiiSize?: BorderRadius
  clearable?: boolean
  onClear?: () => void
}) => {
  const { inputProps, renderElements, onAddButton, triggerProps } =
    selectOptions
  const {
    addOption,
    footer,
    status = 'default',
    loading,
    loadingText = "We're loading the information",
    orientation = 'down',
    openOnFocus = false,
  } = selectProps
  const toggleRef = useRef<HTMLButtonElement>(null)

  const handleFocus = () => {
    inputProps.onFocus()

    if (openOnFocus && !open) {
      toggleRef.current?.click()
    }
  }

  return (
    <StyledSelect>
      <StyledSelectTrigger
        $disabled={disabled}
        {...triggerProps}
        $status={status}
        $borderRadiiSize={borderRadiiSize}
      >
        <Combobox.Input
          {...inputProps}
          onFocus={handleFocus}
          maxLength={selectProps.maxLength}
          style={{
            paddingRight: 0,
            pointerEvents: disabled ? 'none' : 'auto',
            marginRight: '1.5rem',
          }}
          data-testid={
            dataTestId ? `${dataTestId}-input` : 'async-select-input'
          }
        />
        {clearable && (selectOptions.value || selectProps.query) ? (
          <StyledClearButton>
            <Button
              data-testid={
                dataTestId ? `${dataTestId}-clear` : 'async-select-clear'
              }
              endIcon="reject"
              variant="tertiary"
              size="small"
              onClick={() => onClear?.()}
            />
          </StyledClearButton>
        ) : (
          <Combobox.Button
            ref={toggleRef}
            style={{
              background: 'none',
              border: 'none',
              padding: '0',
              margin: '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: disabled ? 'none' : 'auto',
            }}
            data-testid={
              dataTestId ? `${dataTestId}-toggle` : 'async-select-toggle'
            }
          >
            <StyledSelectTriggerDropdown
              style={{
                transform: open ? 'rotate(-180deg)' : '',
                transition: '0.1s all',
                background: 'transparent',
              }}
            >
              <Icon name="dropdown-arrow-down" size={8} color="gray600" />
            </StyledSelectTriggerDropdown>
          </Combobox.Button>
        )}
        <StyledSelectList $orientation={orientation}>
          {loading ? (
            <StyledSelectOptions>
              <Combobox.Options
                style={{
                  margin: 0,
                  padding: 0,
                }}
                static
              >
                <StyledSelectLoading>
                  <Spinner size={4} />
                  <Spacer size={2} /> {loadingText}
                </StyledSelectLoading>
              </Combobox.Options>
            </StyledSelectOptions>
          ) : (
            open && (
              <>
                <StyledSelectOptions>
                  <Combobox.Options
                    style={{
                      margin: 0,
                      padding: 0,
                    }}
                    static
                  >
                    {renderElements}
                  </Combobox.Options>
                </StyledSelectOptions>
                {addOption && (
                  <StyledSelectAddOptionButton
                    onMouseDown={() => {
                      onAddButton()
                      addOption.onClick()
                    }}
                  >
                    {!addOption.hideIcon && (
                      <div style={{ marginRight: '0.5rem' }}>
                        <Icon name="add-circle-solid" size="m" />
                      </div>
                    )}
                    <div
                      style={{
                        marginLeft: addOption.hideIcon ? '0.25rem' : '0',
                      }}
                    >
                      {addOption.label}
                    </div>
                  </StyledSelectAddOptionButton>
                )}
              </>
            )
          )}
          {footer && (
            // Hidden rather than unmounted when closed: the click on a link in the footer is also the
            // outside click that closes the dropdown, and React only runs the link's onClick if it's still mounted.
            <StyledSelectFooter
              hidden={loading || !open}
              data-testid={
                dataTestId ? `${dataTestId}-footer` : 'async-select-footer'
              }
            >
              {footer}
            </StyledSelectFooter>
          )}
        </StyledSelectList>
      </StyledSelectTrigger>
    </StyledSelect>
  )
}

export const AsyncSelect: React.FC<AsyncSelectProps> = (props) => {
  const {
    disabled,
    placeholder,
    noResultsFoundText,
    emptyStateText,
    name,
    options,
    onChange,
    defaultValue,
    query = '',
    setQuery,
    loading: _,
    loadingText: __,
    addOption,
    'data-testid': dataTestId,
    minSearchLength = 0,
    minSearchLengthMessage,
    clearQueryOnSelect = false,
    borderRadiiSize,
    clearable,
  } = props

  const [showMinSearchLengthInfo, setShowMinSearchLengthInfo] = useState(false)

  const handleSetQuery = (query: string) => {
    if (query.length === 0 || query.length >= minSearchLength) {
      setQuery(query)
      setShowMinSearchLengthInfo(false)
    } else {
      setShowMinSearchLengthInfo(true)
    }
  }

  const select = useAsyncSelect({
    options,
    placeholder,
    defaultValue,
    addOptionKeepQuery: addOption?.keepQuery,
    addOptionLabel: addOption?.label,
    keepFieldClearOnAddOptionSelect: addOption?.keepFieldClearOnSelect,
    query,
    setQuery: handleSetQuery,
    noResultsFoundText,
    emptyStateText,
    clearQueryOnSelect,
  })

  const handleClear = () => {
    select.clear()
    onChange?.(null)
  }

  return (
    <Combobox
      disabled={disabled}
      name={name}
      nullable
      value={select.value}
      by={compareOptions}
      defaultValue={defaultValue}
      onChange={(e) => {
        select.setValue(e)
        onChange?.(e)
      }}
    >
      {({ open }) => (
        <>
          <SelectBody
            open={open && !showMinSearchLengthInfo}
            dataTestId={dataTestId}
            selectOptions={select}
            selectProps={props}
            disabled={disabled}
            borderRadiiSize={borderRadiiSize}
            clearable={clearable}
            onClear={handleClear}
          />
          {open && showMinSearchLengthInfo && minSearchLengthMessage && (
            <Typography style={{ textAlign: 'left', width: '100%' }}>
              {minSearchLengthMessage}
            </Typography>
          )}
        </>
      )}
    </Combobox>
  )
}
